import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Package, Clock, CheckCircle, Truck, XCircle, RefreshCcw, AlertTriangle, Wallet, CreditCard } from 'lucide-react';
import axios from 'axios';
import { orderPayable, orderRefundPreview } from '../../utils/orderAmounts';
import { CARD_GRID } from '../../components/customer/customerLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  // Online order whose payment was never completed — shown so the customer can
  // see it wasn't placed, rather than falling back to the "Pending" label.
  awaiting_payment: { label: 'Payment Pending', icon: CreditCard, className: 'status-pending' },
  pending: { label: 'Pending', icon: Clock, className: 'status-pending' },
  confirmed: { label: 'Confirmed', icon: CheckCircle, className: 'status-confirmed' },
  out_for_delivery: { label: 'Out for Delivery', icon: Truck, className: 'status-out-for-delivery' },
  delivered: { label: 'Delivered', icon: CheckCircle, className: 'status-delivered' },
  cancelled: { label: 'Cancelled', icon: XCircle, className: 'status-cancelled' },
};

const CustomerOrders = () => {
  const navigate = useNavigate();
  const { storeSettings } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [refundTo, setRefundTo] = useState('wallet');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/customer/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally { setLoading(false); }
  };

  const handleCancelOrder = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/customer/orders/${cancelModal.id}/cancel`, {
        reason: cancelReason,
        refund_to: refundTo,
      });
      toast.success(res.data.message || 'Order cancelled');
      setCancelModal(null);
      setCancelReason('');
      setRefundTo('wallet');
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally { setCancelling(false); }
  };

  const canCancel = (status) => status === 'pending' || (status === 'confirmed' && !!storeSettings?.allow_cancel_after_confirm);

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const currencySymbol = storeSettings?.currency_symbol || '₹';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <CustomerLayout title="My Orders">
      <div className="p-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {['all', 'pending', 'confirmed', 'out_for_delivery', 'delivered', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`subscription-chip whitespace-nowrap ${filter === status ? 'active' : ''}`}
              data-testid={`filter-${status}`}
            >
              {status === 'all' ? 'All Orders' : STATUS_CONFIG[status]?.label || status}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <div className={`${CARD_GRID} gap-4`}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5 animate-pulse">
                <div className="flex justify-between mb-4">
                  <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-5 bg-gray-200 rounded w-1/4"></div>
                </div>
                <div className="h-4 bg-gray-100 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-[var(--theme-text-muted)] mx-auto mb-4 opacity-30" />
            <p className="text-[var(--theme-text-muted)]">No orders found</p>
            <p className="text-sm text-[var(--theme-text-muted)] mt-1">
              {filter === 'all' ? "Start ordering to see your orders here" : "No orders in this category"}
            </p>
          </div>
        ) : (
          // One order card per row on phones; side by side on desktop.
          <div className={`${CARD_GRID} gap-4`}>
            {filteredOrders.map((order) => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const isSubscription = order.order_type === 'subscription';
              
              return (
                <div key={order.id} className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 overflow-hidden cursor-pointer hover:border-[var(--theme-primary)]/30 transition-all" onClick={() => navigate(`/orders/${order.id}`)} data-testid={`order-${order.id}`}>
                  {/* Order Header */}
                  <div className="p-4 border-b border-black/5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-mono text-xs text-[var(--theme-text-muted)]">
                            #{order.order_number}
                          </p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            isSubscription ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'
                          }`} data-testid={`order-type-${order.id}`}>
                            {isSubscription ? 'Subscription' : 'Order'}
                          </span>
                        </div>
                        <p className="text-sm text-[var(--theme-text-muted)]">
                          {formatDate(order.delivery_date)} • {order.delivery_slot_name}
                        </p>
                      </div>
                      <span className={`status-badge ${statusConfig.className}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Order Items */}
                  <div className="p-4">
                    <div className="space-y-2">
                      {order.items?.slice(0, 3).map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-[var(--theme-text-main)]">
                            {item.quantity}× {item.product_name}
                          </span>
                          <span className="font-medium">{currencySymbol}{item.total}</span>
                        </div>
                      ))}
                      {order.items?.length > 3 && (
                        <p className="text-xs text-[var(--theme-text-muted)]">
                          +{order.items.length - 3} more items
                        </p>
                      )}
                    </div>
                    
                    {/* Total */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/5">
                      <div className="flex items-center gap-2 text-sm text-[var(--theme-text-muted)]">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {order.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                        </span>
                        <span className="capitalize">{order.payment_mode}</span>
                      </div>
                      <span className="font-bold text-lg text-[var(--theme-text-main)]">
                        {currencySymbol}{orderPayable(order)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Delivery Info */}
                  {order.status === 'out_for_delivery' && order.delivery_boy_name && (
                    <div className="px-4 pb-4">
                      <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Truck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            {order.delivery_boy_name} is on the way
                          </p>
                          <p className="text-xs text-blue-700">Your order will arrive soon</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cancel Button */}
                  {canCancel(order.status) && (
                    <div className="px-4 pb-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); setCancelModal(order); }}
                        className="w-full py-2.5 rounded-xl border-2 border-red-200 text-red-600 font-medium text-sm hover:bg-red-50 transition-all"
                        data-testid={`cancel-order-${order.id}`}
                      >
                        Cancel Order
                      </button>
                    </div>
                  )}

                  {/* Cancelled Info */}
                  {order.status === 'cancelled' && order.refund_amount > 0 && (
                    <div className="px-4 pb-4">
                      <div className="bg-red-50 rounded-xl p-3 text-sm">
                        <p className="text-red-700 font-medium">Cancelled</p>
                        {order.cancellation_reason && <p className="text-red-600 text-xs mt-0.5">Reason: {order.cancellation_reason}</p>}
                        <p className="text-red-600 text-xs mt-0.5">
                          Refund: {currencySymbol}{order.refund_amount} to {order.refund_method === 'wallet' ? 'wallet' : 'original source'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Cancel Order Modal */}
        {cancelModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setCancelModal(null)}>
            <div className="bg-[var(--theme-surface)] rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cancel-order-modal">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[var(--theme-text-main)]">Cancel Order</h3>
                  <p className="text-sm text-[var(--theme-text-muted)]">#{cancelModal.order_number}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">Reason (optional)</label>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Why are you cancelling?"
                  className="w-full border border-black/10 rounded-xl p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                  data-testid="cancel-reason-input"
                />
              </div>

              {cancelModal.payment_status === 'paid' && (
                <div>
                  <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">Refund to</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => setRefundTo('wallet')}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        refundTo === 'wallet' ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' : 'border-black/10'
                      }`}
                      data-testid="refund-wallet-btn"
                    >
                      <Wallet className="w-4 h-4" />
                      <span className="text-sm font-medium">Wallet</span>
                    </button>
                    <button
                      onClick={() => setRefundTo('source')}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        refundTo === 'source' ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' : 'border-black/10'
                      }`}
                      data-testid="refund-source-btn"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span className="text-sm font-medium">Original</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Only a paid order is refunded, and by the amount actually paid —
                  previously this always showed the items subtotal, even for an
                  unpaid cash order that has nothing to refund. */}
              {orderRefundPreview(cancelModal) > 0 && (
                <p className="text-sm text-[var(--theme-text-muted)]">
                  Refund amount: <span className="font-bold text-[var(--theme-text-main)]">{currencySymbol}{orderRefundPreview(cancelModal)}</span>
                </p>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCancelModal(null)} className="flex-1">Keep Order</Button>
                <Button onClick={handleCancelOrder} disabled={cancelling} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
                  {cancelling ? 'Cancelling...' : 'Cancel Order'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Refresh hint */}
        <div className="text-center pt-4">
          <button 
            onClick={loadOrders}
            className="text-sm text-[var(--theme-primary)] font-medium flex items-center gap-1 mx-auto"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh Orders
          </button>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerOrders;
